import React, { useEffect, useMemo, useState } from "react";
import "./MaximeterConfigModal.css";
import { useDispatch, useSelector } from "react-redux";
import {
  Settings,
  Info,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Pencil,
} from "lucide-react";
import { Toast } from "../../helpers/alert.js";
import {
  createMaximeterConfig,
  getMaximeterConfigs,
  updateMaximeterConfig,
} from "../../redux/apiCalls/data/maximetroCall";
import { getAllcompanies } from "../../redux/apiCalls/companiesCall/companiesCall.js";
import { getAlldevices } from "../../redux/apiCalls/devicesCall/devicesCall.js";
import { getAllsupplier } from "../../redux/apiCalls/supplierCall/supplierCall.js";

const TZ_AR = "America/Argentina/Buenos_Aires";

const DAYS = [
  { code: "mon", label: "L" },
  { code: "tue", label: "Ma" },
  { code: "wed", label: "Mi" },
  { code: "thu", label: "J" },
  { code: "fri", label: "V" },
  { code: "sat", label: "S" },
  { code: "sun", label: "D" },
];

const PRESETS = {
  SIN_FRANJAS: {
    timeband: [
      {
        code: "ALL",
        name: "Todo el día",
        segments: [
          {
            from: "00:00",
            to: "23:59",
            days: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
          },
        ],
      },
    ],
    billable_band_codes: ["ALL"],
  },

  PUNTA_RESTO_VALLE: {
    timeband: [
      {
        code: "PUNTA",
        name: "Horario Punta",
        segments: [
          {
            from: "10:00",
            to: "14:00",
            days: ["mon", "tue", "wed", "thu", "fri"],
          },
          {
            from: "18:00",
            to: "22:00",
            days: ["mon", "tue", "wed", "thu", "fri"],
          },
        ],
      },
      {
        code: "RESTO",
        name: "Horario Resto",
        segments: [
          {
            from: "08:00",
            to: "10:00",
            days: ["mon", "tue", "wed", "thu", "fri"],
          },
          {
            from: "14:00",
            to: "18:00",
            days: ["mon", "tue", "wed", "thu", "fri"],
          },
          {
            from: "22:00",
            to: "23:59",
            days: ["mon", "tue", "wed", "thu", "fri"],
          },
        ],
      },
      {
        code: "VALLE",
        name: "Horario Valle",
        segments: [
          {
            from: "00:00",
            to: "08:00",
            days: ["mon", "tue", "wed", "thu", "fri"],
          },
          {
            from: "00:00",
            to: "23:59",
            days: ["sat", "sun"],
          },
        ],
      },
    ],
    billable_band_codes: ["PUNTA", "VALLE"],
  },
};

const emptyForm = () => ({
  scope_type: "provider",
  scope_id: "",
  timezone: TZ_AR,
  interval_minutes: 15,
  demand_method: "BLOCK",
  demand_unit: "kW",
  rounding_step: "0.1000",
  rounding_mode: "ROUND_HALF_UP",
  rounding_stage: "FINAL_ONLY",
  missing_data_policy: "IGNORE_INTERVAL",
  max_missing_ratio: "0.0100",
  tie_breaker: "FIRST",
  valid_from: new Date().toISOString().slice(0, 16), // datetime-local
  valid_to: null,
  timeband: [],
  billable_band_codes: [],
});

function safeArray(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      // si vino "PUNTA,RESTO" (mal guardado), lo toleramos
      return v
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    }
  }
  return [];
}

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("es-AR");
  } catch {
    return "—";
  }
}

function normCode(s) {
  return String(s || "")
    .trim()
    .toUpperCase();
}

function isAllDayMode(formData) {
  return (
    Array.isArray(formData.timeband) &&
    formData.timeband.length === 1 &&
    normCode(formData.timeband[0]?.code) === "ALL"
  );
}

function hasRealBands(formData) {
  if (!Array.isArray(formData.timeband)) return false;
  if (formData.timeband.length === 0) return false;
  if (isAllDayMode(formData)) return false; // en modo ALL no mostramos facturables
  // si hay al menos una banda con código no vacío
  return formData.timeband.some((b) => normCode(b?.code));
}

// -------------- Solapamientos (warning, no bloquea) --------------
// Convierte "HH:MM" a minutos
const toMin = (hhmm) => {
  const [h, m] = String(hhmm || "00:00")
    .split(":")
    .map((x) => parseInt(x, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return h * 60 + m;
};

// intervalos de una banda en un día => array de [start,end) en minutos, soporta cruce medianoche
function bandIntervals(band) {
  const from = toMin(band.from);
  const to = toMin(band.to);
  if (from === to) return []; // no tiene sentido
  if (to > from) return [[from, to]];
  // cruza medianoche: ej 18:00 -> 08:00
  return [
    [from, 24 * 60],
    [0, to],
  ];
}

function computeOverlapWarnings(timeband) {
  const warnings = [];

  const expanded = [];

  (timeband || []).forEach((band) => {
    (band.segments || []).forEach((seg) => {
      expanded.push({
        code: normCode(band.code),
        from: seg.from,
        to: seg.to,
        days: seg.days || [],
      });
    });
  });

  for (const day of DAYS.map((d) => d.code)) {
    const daySegs = expanded.filter((s) => s.days.includes(day));

    for (let i = 0; i < daySegs.length; i++) {
      for (let j = i + 1; j < daySegs.length; j++) {
        const a = daySegs[i];
        const b = daySegs[j];

        const aInts = bandIntervals(a);
        const bInts = bandIntervals(b);

        const overlaps = aInts.some(([as, ae]) =>
          bInts.some(([bs, be]) => Math.max(as, bs) < Math.min(ae, be)),
        );

        if (overlaps) {
          warnings.push({
            day,
            a: a.code,
            b: b.code,
          });
        }
      }
    }
  }

  return warnings;
}

const MaximeterConfigModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const suppliers = useSelector((state) => state.supplier.supplier);
  const companies = useSelector((state) => state.companies.companies);
  const devices = useSelector((state) => state.devices.currentDevices);
  const user = useSelector((state) => state.user.currentUser);
  const userToken = user?.accessToken;

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [viewMode, setViewMode] = useState("list"); // "list" | "create" | "edit"
  const [editingConfig, setEditingConfig] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [formData, setFormData] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [activePreset, setActivePreset] = useState(null); // "SIN_FRANJAS" | "PUNTA_RESTO_VALLE" | null

  useEffect(() => {
    if (isOpen && userToken) loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userToken]);

  useEffect(() => {
    if (!isOpen || !userToken) return;

    if (
      formData.scope_type === "provider" &&
      (!suppliers || suppliers.length === 0)
    ) {
      getAllsupplier(dispatch, userToken);
    }

    if (
      formData.scope_type === "branch" &&
      (!companies || companies.length === 0)
    ) {
      getAllcompanies(dispatch, userToken, "branch_offices");
    }

    if (
      formData.scope_type === "device" &&
      (!devices || devices.length === 0)
    ) {
      getAlldevices(dispatch, userToken);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    userToken,
    formData.scope_type,
    suppliers?.length,
    companies?.length,
    devices?.length,
    dispatch,
  ]);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const res = await getMaximeterConfigs(userToken);
      setConfigs(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error("Error loading configs:", error);
      Toast.fire({ icon: "error", title: "Error al cargar configuraciones" });
    } finally {
      setLoading(false);
    }
  };

  const scopeOptions = useMemo(() => {
    if (formData.scope_type === "provider") {
      return (suppliers || []).map((s) => ({
        value: s.id,
        label: s.nombre,
      }));
    }

    if (formData.scope_type === "branch") {
      return (companies || []).map((c) => ({
        value: c.id,
        label: c.name,
      }));
    }

    if (formData.scope_type === "device") {
      return (devices || []).map((d) => ({
        value: d.id,
        label: d.name,
      }));
    }

    return [];
  }, [formData.scope_type, suppliers, companies, devices]);

  const resetForm = () => {
    setFormData(emptyForm());
    setErrors({});
    setAdvancedOpen(false);
    setActivePreset(null);
  };

  const loadConfigForEdit = (config) => {
    const parsedTimeband = safeArray(config.timeband);
    const parsedBillable = safeArray(config.billable_band_codes);

    setFormData({
      scope_type: config.scope_type,
      scope_id: config.scope_id,
      timezone: config.timezone,
      interval_minutes: config.interval_minutes,
      demand_method: config.demand_method,
      demand_unit: config.demand_unit,
      rounding_step: config.rounding_step,
      rounding_mode: config.rounding_mode,
      rounding_stage: config.rounding_stage,
      missing_data_policy: config.missing_data_policy,
      max_missing_ratio: config.max_missing_ratio,
      tie_breaker: config.tie_breaker,
      valid_from: config.valid_from
        ? new Date(config.valid_from).toISOString().slice(0, 16)
        : "",
      valid_to: config.valid_to
        ? new Date(config.valid_to).toISOString().slice(0, 16)
        : null,
      timeband: parsedTimeband,
      billable_band_codes: parsedBillable,
    });

    // Determinar preset si coincide exactamente
    let preset = null;
    if (parsedTimeband.length === 1 && parsedTimeband[0].code === "ALL") {
      preset = "SIN_FRANJAS";
    } else if (
      JSON.stringify(parsedTimeband) ===
      JSON.stringify(PRESETS.PUNTA_RESTO_VALLE.timeband)
    ) {
      preset = "PUNTA_RESTO_VALLE";
    }
    setActivePreset(preset);

    setEditingConfig(config);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const setPreset = (presetKey) => {
    const preset = PRESETS[presetKey];
    if (!preset) return;

    setFormData((prev) => ({
      ...prev,
      timeband: preset.timeband,
      billable_band_codes: preset.billable_band_codes,
    }));
    setActivePreset(presetKey);
    // limpiamos errores relacionados
    setErrors((prev) => ({
      ...prev,
      timeband: null,
      billable_band_codes: null,
    }));
  };

  const toggleAllDayMode = (checked) => {
    if (checked) {
      setPreset("SIN_FRANJAS");
      return;
    }
    // apagar modo: dejamos vacío para que el usuario arme bandas
    setFormData((prev) => ({
      ...prev,
      timeband: [],
      billable_band_codes: [],
    }));
    setActivePreset(null);
  };

  const addTimeband = () => {
    setFormData((prev) => ({
      ...prev,
      timeband: [
        ...(prev.timeband || []),
        {
          code: "",
          name: "",
          segments: [
            {
              from: "00:00",
              to: "23:59",
              days: ["mon", "tue", "wed", "thu", "fri"],
            },
          ],
        },
      ],
    }));
    setActivePreset(null);
  };

  const updateTimeband = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...(prev.timeband || [])];
      if (!updated[index]) return prev;

      updated[index] = {
        ...updated[index],
        [field]: field === "code" ? normCode(value) : value,
      };

      return { ...prev, timeband: updated };
    });

    setActivePreset(null);
  };

  const addSegment = (bandIndex) => {
    setFormData((prev) => {
      const updated = [...(prev.timeband || [])];
      const band = updated[bandIndex];
      if (!band) return prev;

      band.segments = [
        ...(band.segments || []),
        { from: "00:00", to: "23:59", days: [] },
      ];

      return { ...prev, timeband: updated };
    });
  };

  const updateSegment = (bandIndex, segmentIndex, field, value) => {
    setFormData((prev) => {
      const updated = [...(prev.timeband || [])];
      const band = updated[bandIndex];
      if (!band || !band.segments) return prev;

      band.segments[segmentIndex] = {
        ...band.segments[segmentIndex],
        [field]: value,
      };

      return { ...prev, timeband: updated };
    });
  };

  const toggleSegmentDay = (bandIndex, segmentIndex, day) => {
    setFormData((prev) => {
      const updated = [...(prev.timeband || [])];
      const seg = updated[bandIndex]?.segments?.[segmentIndex];
      if (!seg) return prev;

      const days = seg.days || [];
      const nextDays = days.includes(day)
        ? days.filter((d) => d !== day)
        : [...days, day];

      seg.days = nextDays;

      return { ...prev, timeband: updated };
    });
  };

  const removeSegment = (bandIndex, segmentIndex) => {
    setFormData((prev) => {
      const updated = [...(prev.timeband || [])];
      updated[bandIndex].segments = updated[bandIndex].segments.filter(
        (_, i) => i !== segmentIndex,
      );
      return { ...prev, timeband: updated };
    });
  };

  const removeTimeband = (index) => {
    setFormData((prev) => {
      const updated = (prev.timeband || []).filter((_, i) => i !== index);
      const remainingCodes = updated
        .map((b) => normCode(b.code))
        .filter(Boolean);
      const billable = safeArray(prev.billable_band_codes)
        .map(normCode)
        .filter((c) => remainingCodes.includes(c));

      return { ...prev, timeband: updated, billable_band_codes: billable };
    });
    setActivePreset(null);
  };

  const toggleBillable = (code, checked) => {
    const c = normCode(code);
    setFormData((prev) => {
      const current = safeArray(prev.billable_band_codes).map(normCode);
      const next = checked
        ? Array.from(new Set([...current, c])).filter(Boolean)
        : current.filter((x) => x !== c);

      return { ...prev, billable_band_codes: next };
    });
    if (errors.billable_band_codes)
      setErrors((prev) => ({ ...prev, billable_band_codes: null }));
  };

  const overlapWarnings = useMemo(() => {
    if (isAllDayMode(formData)) return [];
    return computeOverlapWarnings(formData.timeband || []);
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.scope_type) newErrors.scope_type = "Requerido";
    if (formData.scope_type !== "general" && !formData.scope_id)
      newErrors.scope_id = "Requerido";

    const bands = Array.isArray(formData.timeband) ? formData.timeband : [];
    const codes = bands.map((b) => normCode(b.code)).filter(Boolean);

    if (!isAllDayMode(formData)) {
      // Si no está en modo ALL, exigimos al menos 1 banda real
      if (bands.length === 0 || codes.length === 0) {
        newErrors.timeband = "Agregue al menos una banda con código y nombre.";
      }
    }

    // duplicados
    if (codes.length && new Set(codes).size !== codes.length) {
      newErrors.timeband = "Códigos de banda duplicados";
    }

    bands.forEach((band, i) => {
      const code = normCode(band.code);
      const name = String(band.name || "").trim();

      if (!code) newErrors[`timeband_${i}_code`] = "Requerido";
      if (!name) newErrors[`timeband_${i}_name`] = "Requerido";

      // validación de rango horario (permitimos cruce medianoche, pero no permitimos igualdad)
      (band.segments || []).forEach((seg, sIndex) => {
        if (!seg.from || !seg.to)
          newErrors[`timeband_${i}_segment_${sIndex}_time`] =
            "Complete desde/hasta";

        if (seg.from === seg.to)
          newErrors[`timeband_${i}_segment_${sIndex}_time`] =
            "Desde/Hasta no pueden ser iguales";

        if (!seg.days || seg.days.length === 0)
          newErrors[`timeband_${i}_segment_${sIndex}_days`] =
            "Seleccione al menos un día";
      });
    });

    // Bandas facturables:
    if (isAllDayMode(formData)) {
      // ALL: autocompleta, no validamos
    } else {
      const billable = safeArray(formData.billable_band_codes)
        .map(normCode)
        .filter(Boolean);

      if (billable.length === 0) {
        newErrors.billable_band_codes =
          "Seleccione al menos una banda facturable";
      } else {
        const invalid = billable.filter((c) => !codes.includes(c));
        if (invalid.length > 0) {
          newErrors.billable_band_codes =
            "Hay bandas facturables que no existen";
        }
      }
    }

    if (formData.valid_to && formData.valid_from >= formData.valid_to) {
      newErrors.valid_to = "Debe ser posterior a 'Desde'";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Normalizamos billable/timeband para DB
    const payload = {
      ...formData,
      timeband: formData.timeband || [],
      billable_band_codes: safeArray(formData.billable_band_codes).map(
        normCode,
      ),
      valid_to: formData.valid_to || null,
      scope_id: formData.scope_type === "general" ? 1 : formData.scope_id,
    };

    try {
      if (viewMode === "create") {
        await createMaximeterConfig(payload, userToken);
        Toast.fire({
          icon: "success",
          title: "Configuración creada exitosamente",
        });
      } else if (viewMode === "edit") {
        await updateMaximeterConfig(editingConfig.id, payload, userToken);
        Toast.fire({
          icon: "success",
          title: "Configuración actualizada exitosamente",
        });
      }
      await loadConfigs();
      setViewMode("list");
      resetForm();
      setEditingConfig(null);
    } catch (error) {
      console.error("Error saving config:", error);
      Toast.fire({ icon: "error", title: "Error al guardar configuración" });
    }
  };

  const backToList = () => {
    setViewMode("list");
    resetForm();
    setEditingConfig(null);
  };

  if (!isOpen) return null;

  return (
    <div
      className="maximeter-config-modal-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className="maximeter-config-modal">
        <div className="modal-header">
          <h2>
            <Settings size={20} /> Configurar
          </h2>
          <button className="close-btn" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* LISTADO */}
          {viewMode === "list" && (
            <div className="configs-section">
              <div className="section-header">
                <div>
                  <h3>Configuraciones existentes</h3>
                  <div className="section-hint">
                    La configuración más específica (Dispositivo {">"} Sucursal{" "}
                    {">"} Proveedor {">"} General) pisa a las demás.
                  </div>
                </div>

                <button
                  className="btn-primary"
                  onClick={() => setViewMode("create")}
                >
                  <Plus size={16} /> Nueva configuración
                </button>
              </div>

              {loading ? (
                <div className="loading">Cargando...</div>
              ) : (
                <div className="configs-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Alcance</th>
                        <th>Nombre</th>
                        <th>Intervalo</th>
                        <th>Método</th>
                        <th>Bandas facturables</th>
                        <th>Vigencia</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(configs || []).map((config) => {
                        const billable = safeArray(
                          config.billable_band_codes,
                        ).map(normCode);
                        return (
                          <tr key={config.id}>
                            <td>
                              <span className="pill pill-scope">
                                {config?.scope_type === "provider"
                                  ? "Proveedor"
                                  : config?.scope_type === "branch"
                                    ? "Sucursal"
                                    : "Dispositivo"}
                              </span>
                            </td>
                            <td>{config?.provider_name}</td>
                            <td>{config?.interval_minutes} min</td>
                            <td>{config?.demand_method}</td>
                            <td>
                              {billable.length ? (
                                <div className="pillRow">
                                  {billable?.map((c) => (
                                    <span key={c} className="pill pill-band">
                                      {c}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </td>
                            <td>
                              {fmtDate(config.valid_from)}
                              {config.valid_to
                                ? ` - ${fmtDate(config.valid_to)}`
                                : ""}
                            </td>
                            <td>
                              <button
                                className="btn-icon"
                                onClick={() => {
                                  loadConfigForEdit(config);
                                  setViewMode("edit");
                                }}
                                title="Editar configuración"
                              >
                                <Pencil size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}

                      {(!configs || configs.length === 0) && (
                        <tr>
                          <td colSpan="7" className="no-data">
                            No hay configuraciones
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {/* FORM */}
          {viewMode !== "list" && (
            <div className="form-section">
              <div className="formTopBar">
                <div>
                  <h3>
                    {viewMode === "create"
                      ? "Creando configuración"
                      : "Editando configuración"}
                  </h3>
                  <div className="section-hint">
                    {viewMode === "create"
                      ? "Definí el alcance, armá las franjas horarias (si aplica) y seleccioná qué bandas son facturables."
                      : "Modificá los parámetros de la configuración existente."}
                  </div>
                </div>

                <button
                  className="btn-ghost"
                  onClick={backToList}
                  type="button"
                >
                  <X size={16} /> Volver al listado
                </button>
              </div>

              {/* ======= BLOQUE 0: ALCANCE ======= */}
              <div className="sectionCard">
                <div className="sectionCardHeader">
                  <div className="sectionCardTitle">
                    1) Alcance
                    <span
                      className="infoIcon"
                      title="General aplica a todos. Proveedor/Sucursal/Dispositivo pisan a General."
                    >
                      <Info size={14} />
                    </span>
                  </div>
                </div>

                <div className="grid2">
                  <div className="form-group compact">
                    <label>
                      Alcance <span className="required">*</span>
                    </label>
                    <select
                      value={formData.scope_type}
                      onChange={(e) =>
                        handleInputChange("scope_type", e.target.value)
                      }
                    >
                      <option value="provider">Proveedor</option>
                    </select>
                    {errors.scope_type && (
                      <span className="error">{errors.scope_type}</span>
                    )}
                  </div>

                  {formData.scope_type !== "general" ? (
                    <div className="form-group compact">
                      <label>
                        ID del alcance <span className="required">*</span>
                      </label>
                      <select
                        value={formData.scope_id || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "scope_id",
                            parseInt(e.target.value, 10),
                          )
                        }
                      >
                        <option value="">Seleccione...</option>
                        {scopeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {errors.scope_id && (
                        <span className="error">{errors.scope_id}</span>
                      )}
                    </div>
                  ) : (
                    <div className="form-group compact">
                      <label>ID del alcance</label>
                      <input type="text" value="1 (General)" disabled />
                    </div>
                  )}
                </div>
              </div>

              {/* ======= BLOQUE 1: PARAMETROS ======= */}
              <div className="sectionCard">
                <div className="sectionCardHeader">
                  <div className="sectionCardTitle">2) Parámetros técnicos</div>
                  <button
                    type="button"
                    className="toggle-advanced"
                    onClick={() => setAdvancedOpen(!advancedOpen)}
                  >
                    {advancedOpen ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                    Avanzado
                  </button>
                </div>

                <div className="tech-params">
                  <div className="param-row">
                    <label>Intervalo (min)</label>
                    <select
                      value={formData.interval_minutes}
                      onChange={(e) =>
                        handleInputChange(
                          "interval_minutes",
                          parseInt(e.target.value, 10),
                        )
                      }
                    >
                      <option value={15}>15</option>
                      <option value={30}>30</option>
                      <option value={60}>60</option>
                    </select>
                  </div>

                  <div className="param-row">
                    <label>Método de demanda</label>
                    <select
                      value={formData.demand_method}
                      onChange={(e) =>
                        handleInputChange("demand_method", e.target.value)
                      }
                    >
                      <option value="BLOCK">BLOCK</option>
                    </select>
                  </div>
                </div>

                {advancedOpen && (
                  <div className="advanced-params">
                    <div className="param-row">
                      <label>Timezone</label>
                      <input
                        type="text"
                        value={formData.timezone}
                        onChange={(e) =>
                          handleInputChange("timezone", e.target.value)
                        }
                      />
                    </div>

                    <div className="param-row">
                      <label>Unidad de demanda</label>
                      <input
                        type="text"
                        value={formData.demand_unit}
                        onChange={(e) =>
                          handleInputChange("demand_unit", e.target.value)
                        }
                      />
                    </div>

                    <div className="param-row">
                      <label>Paso de redondeo</label>
                      <input
                        type="text"
                        value={formData.rounding_step}
                        onChange={(e) =>
                          handleInputChange("rounding_step", e.target.value)
                        }
                      />
                    </div>

                    <div className="param-row">
                      <label>Modo de redondeo</label>
                      <select
                        value={formData.rounding_mode}
                        onChange={(e) =>
                          handleInputChange("rounding_mode", e.target.value)
                        }
                      >
                        <option value="ROUND_HALF_UP">ROUND_HALF_UP</option>
                        <option value="TRUNCATE">TRUNCATE</option>
                        <option value="BANKERS">BANKERS</option>
                      </select>
                    </div>

                    <div className="param-row">
                      <label>Etapa de redondeo</label>
                      <input
                        type="text"
                        value={formData.rounding_stage}
                        onChange={(e) =>
                          handleInputChange("rounding_stage", e.target.value)
                        }
                      />
                    </div>

                    <div className="param-row">
                      <label>Política datos faltantes</label>
                      <select
                        value={formData.missing_data_policy}
                        onChange={(e) =>
                          handleInputChange(
                            "missing_data_policy",
                            e.target.value,
                          )
                        }
                      >
                        <option value="IGNORE_INTERVAL">IGNORE_INTERVAL</option>
                        <option value="ERROR">ERROR</option>
                      </select>
                    </div>

                    <div className="param-row">
                      <label>Ratio máx. faltante</label>
                      <input
                        type="text"
                        value={formData.max_missing_ratio}
                        onChange={(e) =>
                          handleInputChange("max_missing_ratio", e.target.value)
                        }
                      />
                    </div>

                    <div className="param-row">
                      <label>Desempate</label>
                      <select
                        value={formData.tie_breaker}
                        onChange={(e) =>
                          handleInputChange("tie_breaker", e.target.value)
                        }
                      >
                        <option value="FIRST">FIRST</option>
                        <option value="LAST">LAST</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* ======= BLOQUE 2: TOU ======= */}
              <div className="sectionCard">
                <div className="sectionCardHeader">
                  <div className="sectionCardTitle">
                    3) Franjas horarias (TOU)
                    <span
                      className="infoIcon"
                      title="Si el proveedor NO factura por franjas, activá 'Banda única'. Si factura por franjas, definí bandas sin solaparse."
                    >
                      <Info size={14} />
                    </span>
                  </div>
                </div>

                <div className="touTopRow">
                  <label className="switchRow">
                    <input
                      type="checkbox"
                      checked={isAllDayMode(formData)}
                      onChange={(e) => toggleAllDayMode(e.target.checked)}
                    />
                    <span className="switchText">
                      El proveedor NO factura por franjas (banda única)
                      <span className="switchSub">
                        Se guardará una única banda ALL (00:00–23:59) como
                        facturable.
                      </span>
                    </span>
                  </label>

                  {!isAllDayMode(formData) && (
                    <div className="presetRow">
                      <button
                        type="button"
                        className={`btn-chip ${activePreset === "PUNTA_RESTO_VALLE" ? "active" : ""}`}
                        onClick={() => setPreset("PUNTA_RESTO_VALLE")}
                      >
                        <Check size={14} /> Preset PUNTA/RESTO/VALLE
                      </button>

                      <button
                        type="button"
                        className="btn-chip"
                        onClick={addTimeband}
                      >
                        <Plus size={14} /> Agregar banda
                      </button>
                    </div>
                  )}
                </div>

                {errors.timeband && (
                  <div className="banner bannerError">{errors.timeband}</div>
                )}

                {/* warnings de solapamiento */}
                {!isAllDayMode(formData) && overlapWarnings.length > 0 && (
                  <div className="banner bannerWarn">
                    <strong>Advertencia:</strong> se detectaron posibles
                    solapamientos entre bandas (revisar horarios/días).
                  </div>
                )}

                {/* constructor */}
                {!isAllDayMode(formData) && (
                  <div className="timebands">
                    {(formData.timeband || []).map((band, index) => {
                      const code = normCode(band.code);
                      const name = String(band.name || "");

                      return (
                        <div key={index} className="timeband-card">
                          <div className="band-header">
                            <div className="bandInputs">
                              <div className="bandField">
                                <label>Código de banda</label>
                                <input
                                  type="text"
                                  placeholder="Ej: PUNTA"
                                  value={code}
                                  onChange={(e) =>
                                    updateTimeband(
                                      index,
                                      "code",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>

                              <div className="bandField">
                                <label>Nombre descriptivo</label>
                                <input
                                  type="text"
                                  placeholder="Ej: Horario Punta"
                                  value={name}
                                  onChange={(e) =>
                                    updateTimeband(
                                      index,
                                      "name",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                            </div>

                            <button
                              type="button"
                              className="remove-band"
                              onClick={() => removeTimeband(index)}
                              title="Eliminar banda"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>

                          {(band.segments || []).map((seg, sIndex) => (
                            <div key={sIndex} className="segment-card">
                              <div className="band-times">
                                <div className="cont-timefields">
                                  <div className="timeField">
                                    <label>Desde</label>
                                    <input
                                      type="time"
                                      value={seg.from}
                                      onChange={(e) =>
                                        updateSegment(
                                          index,
                                          sIndex,
                                          "from",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </div>

                                  <div className="timeField">
                                    <label>Hasta</label>
                                    <input
                                      type="time"
                                      value={seg.to}
                                      onChange={(e) =>
                                        updateSegment(
                                          index,
                                          sIndex,
                                          "to",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="remove-segment"
                                  onClick={() => removeSegment(index, sIndex)}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>

                              <div className="band-days">
                                {DAYS.map((d) => (
                                  <button
                                    key={d.code}
                                    type="button"
                                    className={`day-btn ${
                                      seg.days?.includes(d.code) ? "active" : ""
                                    }`}
                                    onClick={() =>
                                      toggleSegmentDay(index, sIndex, d.code)
                                    }
                                  >
                                    {d.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}

                          <button
                            type="button"
                            className="btn-chip"
                            onClick={() => addSegment(index)}
                          >
                            <Plus size={14} /> Agregar tramo
                          </button>

                          <div className="bandInlineErrors">
                            {errors[`timeband_${index}_code`] && (
                              <span className="error">
                                {errors[`timeband_${index}_code`]}
                              </span>
                            )}
                            {errors[`timeband_${index}_name`] && (
                              <span className="error">
                                {errors[`timeband_${index}_name`]}
                              </span>
                            )}
                            {errors[`timeband_${index}_time`] && (
                              <span className="error">
                                {errors[`timeband_${index}_time`]}
                              </span>
                            )}
                            {errors[`timeband_${index}_days`] && (
                              <span className="error">
                                {errors[`timeband_${index}_days`]}
                              </span>
                            )}
                          </div>

                          {/* hint solapamiento por banda */}
                          {overlapWarnings.length > 0 && code && (
                            <div className="bandHint">
                              {overlapWarnings.some(
                                (w) => w.a === code || w.b === code,
                              ) ? (
                                <span className="hintWarn">
                                  Revisar solapamientos con otras bandas.
                                </span>
                              ) : (
                                <span className="hintOk">OK</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {(!formData.timeband || formData.timeband.length === 0) && (
                      <div className="emptyState">
                        <div className="emptyTitle">Todavía no hay bandas</div>
                        <div className="emptySub">
                          Agregá una banda o aplicá el preset. Luego marcá
                          cuáles son facturables.
                        </div>
                        <button
                          type="button"
                          className="btn-chip"
                          onClick={addTimeband}
                        >
                          <Plus size={14} /> Agregar banda
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* modo ALL: mostramos resumen */}
                {isAllDayMode(formData) && (
                  <div className="allDayCard">
                    <div className="pillRow">
                      <span className="pill pill-band">ALL</span>
                      <span className="muted">Todo el día (00:00–23:59)</span>
                    </div>
                    <div className="muted" style={{ marginTop: 6 }}>
                      Bandas facturables: <strong>ALL</strong>
                    </div>
                  </div>
                )}
              </div>

              {/* ======= BLOQUE 3: FACTURABLES ======= */}
              {!isAllDayMode(formData) && (
                <div className="sectionCard">
                  <div className="sectionCardHeader">
                    <div className="sectionCardTitle">
                      4) Bandas facturables
                      <span
                        className="infoIcon"
                        title="Seleccioná las bandas que el proveedor efectivamente factura. Puede ser una o varias."
                      >
                        <Info size={14} />
                      </span>
                    </div>
                  </div>

                  {!hasRealBands(formData) ? (
                    <div className="emptyInline">
                      Definí al menos una banda con código para poder marcar
                      bandas facturables.
                    </div>
                  ) : (
                    <>
                      <div className="billableGrid">
                        {(formData.timeband || [])
                          .map((b) => ({
                            code: normCode(b.code),
                            name: String(b.name || "").trim(),
                          }))
                          .filter((b) => b.code)
                          .map((b) => (
                            <label key={b.code} className="billableItem">
                              <input
                                type="checkbox"
                                checked={safeArray(formData.billable_band_codes)
                                  .map(normCode)
                                  .includes(b.code)}
                                onChange={(e) =>
                                  toggleBillable(b.code, e.target.checked)
                                }
                              />
                              <div className="billableText">
                                <div className="billableCode">{b.code}</div>
                                <div className="billableName">
                                  {b.name || "—"}
                                </div>
                              </div>
                            </label>
                          ))}
                      </div>

                      {errors.billable_band_codes && (
                        <span className="error">
                          {errors.billable_band_codes}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ======= BLOQUE 4: VIGENCIA ======= */}
              <div className="sectionCard">
                <div className="sectionCardHeader">
                  <div className="sectionCardTitle">5) Vigencia</div>
                </div>

                <div className="validity-row">
                  <div className="form-group compact">
                    <label>
                      Desde <span className="required">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.valid_from}
                      onChange={(e) =>
                        handleInputChange("valid_from", e.target.value)
                      }
                    />
                    {errors.valid_from && (
                      <span className="error">{errors.valid_from}</span>
                    )}
                  </div>

                  <div className="form-group compact">
                    <label>Hasta (opcional)</label>
                    <input
                      type="datetime-local"
                      value={formData.valid_to || ""}
                      onChange={(e) =>
                        handleInputChange("valid_to", e.target.value || null)
                      }
                    />
                    {errors.valid_to && (
                      <span className="error">{errors.valid_to}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* ======= ACCIONES ======= */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={backToList}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSave}
                >
                  {viewMode === "create" ? "Guardar" : "Guardar cambios"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaximeterConfigModal;
